import React, { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, X, RotateCw } from 'lucide-react';

// Configuration du worker PDF.js - Version fixe
pdfjs.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

interface PDFViewerProps {
  pdfUrl: string;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ 
  pdfUrl, 
  isOpen, 
  onClose, 
  title = "Guide d'utilisation" 
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [rotation, setRotation] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Log pour diagnostiquer
  React.useEffect(() => {
    if (isOpen && pdfUrl) {
      console.log('PDFViewer ouvert avec URL:', pdfUrl);
      console.log('Worker configuré:', pdfjs.GlobalWorkerOptions.workerSrc);
      setIsLoading(true);
      setError(null);
      setPageNumber(1);
      setNumPages(0);
    }
  }, [isOpen, pdfUrl]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsLoading(false);
    setError(null);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('Erreur lors du chargement du PDF:', error);
    console.error('URL du PDF:', pdfUrl);
    setError(`Erreur lors du chargement du document PDF: ${error.message}`);
    setIsLoading(false);
  }, [pdfUrl]);

  const goToPrevPage = useCallback(() => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber(prev => Math.min(prev + 1, numPages));
  }, [numPages]);

  const zoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  }, []);

  const zoomOut = useCallback(() => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  }, []);

  const rotate = useCallback(() => {
    setRotation(prev => (prev + 90) % 360);
  }, []);

  const downloadPDF = useCallback(() => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'guide-utilisation.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [pdfUrl]);

  const handlePageInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const page = parseInt(e.target.value);
    if (page >= 1 && page <= numPages) {
      setPageNumber(page);
    }
  }, [numPages]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-2xl w-full h-full max-w-6xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          <div className="flex items-center space-x-2">
            {/* Controls */}
            <div className="flex items-center space-x-1 bg-white rounded-lg px-3 py-1 border">
              <button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Page précédente"
              >
                <ChevronLeft size={16} />
              </button>
              
              <div className="flex items-center space-x-1 text-sm">
                <input
                  type="number"
                  value={pageNumber}
                  onChange={handlePageInputChange}
                  className="w-12 text-center border rounded px-1 py-0.5"
                  min={1}
                  max={numPages}
                />
                <span className="text-gray-500">/ {numPages}</span>
              </div>
              
              <button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Page suivante"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Zoom controls */}
            <div className="flex items-center space-x-1 bg-white rounded-lg px-2 py-1 border">
              <button
                onClick={zoomOut}
                className="p-1 rounded hover:bg-gray-100"
                title="Zoom arrière"
              >
                <ZoomOut size={16} />
              </button>
              <span className="text-sm text-gray-600 min-w-[3rem] text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={zoomIn}
                className="p-1 rounded hover:bg-gray-100"
                title="Zoom avant"
              >
                <ZoomIn size={16} />
              </button>
            </div>

            {/* Rotate button */}
            <button
              onClick={rotate}
              className="p-2 bg-white rounded-lg border hover:bg-gray-100"
              title="Rotation"
            >
              <RotateCw size={16} />
            </button>

            {/* Download button */}
            <button
              onClick={downloadPDF}
              className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              title="Télécharger"
            >
              <Download size={16} />
            </button>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              title="Fermer"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* PDF Content */}
        <div className="flex-1 overflow-auto bg-gray-100 p-4">
          <div className="flex justify-center">
            {isLoading && (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-600">Chargement du document...</span>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="text-red-500 text-lg mb-2">⚠️</div>
                  <p className="text-red-600">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-3 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            )}

            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              onLoadProgress={({ loaded, total }) => {
                console.log('Progression du chargement:', loaded, '/', total);
              }}
              options={{
                cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
                cMapPacked: true,
                standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/standard_fonts/',
              }}
            >
              {numPages > 0 && (
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  rotate={rotation}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-lg"
                  onLoadSuccess={() => console.log('Page chargée avec succès')}
                  onLoadError={(error) => console.error('Erreur page:', error)}
                />
              )}
            </Document>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <span>Utilisez les contrôles ci-dessus pour naviguer dans le document</span>
            <span>Zoom: {Math.round(scale * 100)}% | Page: {pageNumber}/{numPages}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
